import type { DialogsState } from "./dialog/sysdialogbase"
import type { SYSFormState } from "./forms/actions"

import type {
  DirectiveResult,
  SYSActions,
  SYSDialog,
  SYSForm,
  SYSScreen,
  SYSState,
} from ".."

import { currentScreenAndDialog, render, TemplateResult } from ".."

import { ovl, SYSConfig } from ".."
import {
  disableBodyScroll,
  enableBodyScroll,
  hasOSReducedMotion,
  isMobile,
} from "../global/globals"

import { screenFadedOutResolver } from "../global/actions"
import { actionTracking } from "../tracker/proxyhandler"
import {
  _RenderTracking,
  callbacks,
  disposeTrack,
  startTrack,
  stopTrack,
} from "../tracker/tracker"
import { _setDialogVisible } from "./forms/helper"
import { ScreenCurrent, ScreenCurrentState } from "./screenapi"
import { FieldSetVal } from "./forms/fieldapi"
import { SnackAdd } from "./snackapi"

type ScreensHistory = { screen: SYSScreen; instance: string }[]

export type _FocusState = {
  focusSet?: boolean
  focusWhenLeft?: string
}

export type _ScreenState = {
  screen: SYSScreen
  instance?: string
  lastScrollTop?: number
  disabled?: boolean
  visible?: boolean
  closing?: boolean
  focus?: _FocusState
}

export type ScreensState = {
  screens: {
    [key in SYSScreen]: {
      tag?: string
      noHistory?: boolean
      screenCloseBehaviour?: ScreenCloseBehaviour
      instance: { [key: string]: _ScreenState }
    }
  }
  nav: NavState
}

export type NavState = {
  nextScreen: SYSScreen
  nextScreenInstance: string
  previousScreen: SYSScreen
  previousScreenInstance: string
  screenCounter: number
  dialogCounter: number
  screensHistory: ScreensHistory
  isNavigating: boolean
}
export type ScreenCloseBehaviour = "remove" | "ovl-hide"
let _ovl: SYSState
export const setLastScrollPosition = (screens: typeof _ovl.ovl.screens) => {
  let currentScreen = screens.nav.previousScreen
  let currentScreenContext = screens.nav.previousScreenInstance
  if (currentScreen) {
    // get the first scrollable class of the doc
    let o = screens.screens[currentScreen].instance[currentScreenContext]

    let scrollable: HTMLElement
    if (isMobile()) {
      scrollable = document.querySelector(".scrollableMobile")
    } else {
      scrollable = document.querySelector(".scrollable")
    }
    //if (scrollable.tagName === "BODY") {
    if (!scrollable) {
      scrollable = document.documentElement
    }
    //   }
    // and remember the scroll pos
    if (scrollable && scrollable.scrollTop) {
      //console.log("saveScroll:", currentScreen, scrollable.scrollTop)
      o.lastScrollTop = scrollable.scrollTop
    } else {
      o.lastScrollTop = undefined
    }
  }
}

export const scrollToLastPosition = (screens: typeof _ovl.ovl.screens) => {
  if (!screens.nav) {
    return
  }
  let currentScreenState = ScreenCurrentState()
  let screen = currentScreenState.screen
  let instance = currentScreenState.instance
  if (!screens.screens?.[screen]?.instance?.[instance]) {
    return
  }

  let lastScrollTop = screens.screens[screen].instance[instance].lastScrollTop
  //console.log("scrollTo:", screen, lastScrollTop)

  if (lastScrollTop === undefined) {
    return
  }
  // set scroll to remembered pos
  let scrollable
  if (isMobile()) {
    scrollable = document.querySelector(".scrollableMobile")
  } else {
    scrollable = document.querySelector(".scrollable")
  }

  // if (scrollable.tagName === "BODY") {
  if (!scrollable) {
    scrollable = document.documentElement
  }
  //}

  if (scrollable) {
    if (!lastScrollTop) {
      lastScrollTop = 0
    }
    scrollable.scrollTo({
      top: lastScrollTop,
      left: 0,
      behavior: "smooth",
    })
  }
}

export class SYSBaseElement extends HTMLElement {
  zIndex: number
  state: SYSState
  actions: SYSActions
  formType: SYSForm
  formId: string
  formState: SYSFormState
  screen: SYSScreen
  screenInstance: string
  screenState: _ScreenState
  closedCallbackFn: any
  dialog: SYSDialog
  dialogInstance: any
  isDialogHolder: boolean
  dialogState: DialogsState
  uiState: typeof _ovl.ovl.uiState
  reInitWhenCalledFromScreenEnterUnhide: boolean
  _initialised: boolean
  name: string
  _id: number = 0

  screenCloseBehaviour: ScreenCloseBehaviour

  updatedDebouncer: any

  firstUpdatedCalled: boolean

  handleAnimationStart = (e) => {
    if (e.animationName === "fadeInScreen") {
      scrollToLastPosition(this.state.ovl.screens)
    }
  }

  handleAnimationEnd = (e) => {
    if (e.animationName === "fadeOutScreen") {
      e.target.classList.remove("trackingBlock")
      if (screenFadedOutResolver) {
        screenFadedOutResolver()
      }
    } else if (e.animationName === "fadeInScreen") {
      e.target.classList.remove("fadeInScreen")
    } else if (e.animationName === "fadeOutDialog" && this.dialogState) {
      _setDialogVisible(false, this.dialogState)
    }
  }

  async getUI(): Promise<
    TemplateResult[] | TemplateResult | HTMLElement | DirectiveResult
  > {
    return undefined
  }
  async init(fromScreenEnterUnhide?: string): Promise<any> {
    // use it for basic initialisiation
    // lets say to define screen or dialog
    // fromScreenEnterUnhide will be true if called when Screen was ovl-hide (ScreenBehaviour "Hide")
  }

  async afterRender() {
    // use it for customisation after every rerender
  }

  async updated() {
    // use it for customisation after every rerender and when dom is ready
  }

  async firstUpdated() {
    // use it for customisation after first rerender and when dom is ready (set focus eg.)
    // focus last focused element when reenter the screen
    // console.log("first Updated", this.name)
    // let focusId
    // if (this.screenState) {
    //   if (
    //     !this.screenState.focus.focusSet &&
    //     this.screenState.focus.focusWhenReEnter
    //   ) {
    //     focusId = this.screenState.focus.focusWhenReEnter
    //     this.screenState.focus.focusSet = true
    //   }
    //   if (!this.screenState.focus.focusSet && !focusId) {
    //     focusId = `ovlscreen_${this.screen}`
    //   }
    // } else if (
    //   this.isDialogHolder &&
    //   this.dialogState &&
    //   !this.dialogState.focus.focusSet
    // ) {
    //   focusId = this.dialogState.focus.focusWhenReEnter
    //   if (!focusId && this.dialogState.focus.forceElementIdToFocusAfterOpen) {
    //     focusId = this.dialogState.focus.forceElementIdToFocusAfterOpen
    //     this.dialogState.focus.forceElementIdToFocusAfterOpen = undefined
    //   }
    //   this.dialogState.focus.focusSet = true
    //   if (!focusId) {
    //     focusId = `ovl-dialog-${this.dialog}-${this.dialogInstance}`
    //   }
    // }
    // if (focusId) {
    //   FocusSet(focusId)
    // }
  }

  constructor() {
    super()
    this._id = new Date().getTime()
    this.name = `${this.localName}${this._id.toString()}`
    this.state = ovl.getState()
    this.actions = ovl.getActions()
    this.uiState = ovl.getState().ovl.uiState
  }

  async doRender() {
    if (!this.isConnected) {
      stopTrack()
      return
    }
    startTrack(this)
    if (SYSConfig._system.debugTracking) {
      console.log(
        "render %c" + `id: ${this.id}, name:${this.name}`,
        "color:green"
      )
    }
    //return await this.track(async () => {
    // some necessary trackings
    //resumeTracking()

    if (!this.isDialogHolder) {
      if (this.screenState) {
        this.screenState.visible
        this.screenState.disabled
      }
      if (this.dialogState) {
        this.dialogState.closing
        this.dialogState.visible
        if (this.dialogState.disabled) {
          if (!this.classList.contains("ovl-disabled")) {
            this.classList.add("ovl-disabled")
          }
        } else {
          if (this.classList.contains("ovl-disabled")) {
            this.classList.remove("ovl-disabled")
          }
        }
      }
    }
    // no need for screen or dialog rerendering if fading out
    if (
      (this.screen || this.dialog) &&
      this.classList.toString().indexOf("fadeOut") > -1
    ) {
      //pauseTracking()
      stopTrack()
      return
    }

    // /necessary trackings
    //pauseTracking()

    let dialogVisible = false
    let screenVisible = false

    if (this.dialog) {
      // if (!this.isDialogHolder) {
      //   this.id = "ovldialog_" + this.dialog
      // }
      if (this.dialogState.closing) {
        if (this.getElementsByClassName("fd-message-box").length < 1) {
          //pauseTracking()
          stopTrack()
          return
        }

        this.doDialogOnCloseStuff()

        if (hasOSReducedMotion()) {
          _setDialogVisible(false, this.dialogState)
        } else {
          //if (!this.isDialogHolder) {
          this.fadeOut("fadeInDialog", "fadeOutDialog")
          //}
        }
        //pauseTracking()
        stopTrack()
        return
      }
      dialogVisible = this.dialogState.visible
    } else if (this.screen) {
      screenVisible = this.screenState.visible
    }
    //resumeTracking()

    let isComponent = !this.screen && !this.dialog

    let res
    let isScreenAndVisible = screenVisible && this.screen
    let isDialogAndVisible = dialogVisible && this.dialog
    if (isScreenAndVisible || isDialogAndVisible) {
      if (this.screen) {
        if (this.screenState.disabled) {
          if (!this.classList.contains("ovl-disabled")) {
            this.classList.add("ovl-disabled")
          }
        } else {
          if (this.classList.contains("ovl-disabled")) {
            this.classList.remove("ovl-disabled")
          }
        }
      }
    }

    if (isComponent || isScreenAndVisible || isDialogAndVisible) {
      actionTracking.lastActionName = `Component(${actionTracking.lastActionName}): ${this.id}`
      startTrack(this)
      res = await this.getUI()
      stopTrack()

      if (res) {
        if (this.dialog) {
          if (!this.isDialogHolder) {
            disableBodyScroll()
          }
        }
        let startTime
        let endTime
        startTime = new Date().getTime()
        await render(res, this)
        endTime = new Date().getTime()
        if (SYSConfig._system.debugTracking) {
          console.log(
            "render %c" +
              `id: ${this.id}, name:${this.name}, time elapsed MS:${
                endTime - startTime
              }`,
            "color:green"
          )
        }
        // "new" approach do afterrender only if smth was rendered
        // needs some long term testing and can then be removed (the flag)
        if (SYSConfig._system.suppressAfterRenderIfGetUIEmpty) {
          await this.doAfterRender()
        }
      } else {
        await render(undefined, this)
      }
    }

    // "old" approach do afterrender even there was nothing rendered
    if (!SYSConfig._system.suppressAfterRenderIfGetUIEmpty) {
      await this.doAfterRender()
    }

    //pauseTracking()
  }

  doAfterRender = async () => {
    if (this.isConnected) {
      await this.afterRender()
      if (this.updatedDebouncer) {
        clearTimeout(this.updatedDebouncer)
        //this.updatedDebouncer = null
      }
      this.updatedDebouncer = setTimeout(async () => {
        if (this.isConnected) {
          if (!this.firstUpdatedCalled) {
            this.firstUpdatedCalled = true
            await this.firstUpdated()
          }
          await this.updated()
        }
      }, 150)
    }
  }

  doDialogOnCloseStuff() {
    enableBodyScroll()
  }

  fadeOut(fadeInClass: string, fadeOutClass: string) {
    let el: any = this
    if (this.dialog) {
      el = this.getElementsByClassName("fd-message-box")[0]
    }
    if (el.classList.contains(fadeInClass)) {
      el.classList.remove(fadeInClass)
    }
    if (!el.classList.contains("ovl-disabled")) {
      el.classList.add("ovl-disabled")
    }
    if (!el.classList.contains(fadeOutClass)) {
      el.classList.add(fadeOutClass)
    }
  }

  fadeIn(fadeInClass: string, fadeOutClass: string) {
    let el: any = this
    if (el.classList.contains(fadeOutClass)) {
      el.classList.remove(fadeOutClass)
    }
    if (!el.classList.contains(fadeInClass)) {
      el.classList.add(fadeInClass)
    }
  }

  async connectedCallback() {
    _RenderTracking.pauseTracking = true
    if (this.screen) {
      this.screenState =
        this.state.ovl.screens.screens[this.screen].instance[
          this.screenInstance
        ]
      this.screenCloseBehaviour =
        this.state.ovl.screens.screens[this.screen].screenCloseBehaviour
    }

    if (this.dialog && !this.dialogInstance) {
      this.dialogInstance = this.dialog
    }

    if (this.dialog) {
      this.dialogState =
        this.state.ovl.dialogs[this.dialog].instance[this.dialogInstance]
      // if (!this.dialogState.zIndex) {
      //   let dlgsCount = Object.keys(currentScreenAndDialog.dialogs).length
      //   this.dialogState.zIndex = 6 + dlgsCount
      // }
      this.zIndex = this.dialogState.zIndex
    }

    if (SYSConfig._system.debugTracking) {
      console.log(
        "init %c" + `id: ${this.id}, name:${this.name}`,
        "color:green"
      )
    }
    this.classList.add("ovl-comp")

    if (!(await this.init())) {
      await this.doRender()
    }
    this._initialised = true
    if (
      this.reInitWhenCalledFromScreenEnterUnhide &&
      !this.classList.contains("ovl-reinitwhenreenter")
    ) {
      this.classList.add("ovl-reinitwhenreenter")
    }
    if (this.screen || this.dialog) {
      if (!this.isDialogHolder) {
        this.addEventListener("animationend", this.handleAnimationEnd)
        this.addEventListener("animationstart", this.handleAnimationStart)
      }
    }
    this.addEventListener("focusout", this.handleFocusOutAutoFillHack)
  }

  async disconnectedCallback() {
    this.removeEventListener("animationend", this.handleAnimationEnd)
    this.removeEventListener("animationstart", this.handleAnimationStart)
    this.removeEventListener("focusout", this.handleFocusOutAutoFillHack)

    //pauseTracking()
    disposeTrack(this)
    if (SYSConfig._system.debugTracking) {
      console.log(
        "disconnect %c" + `id: ${this.id}, name:${this.name}`,
        "color:green"
      )
    }
    if (this.dialogState) {
      if (this.closedCallbackFn) {
        this.closedCallbackFn()
        this.closedCallbackFn = null
      }
    }
  }

  async handleFocusOutAutoFillHack(e) {
    // <autofill hack>
    // take care of all autofill fields (test with ios especially)
    if (this.formState) {
      Object.keys(this.formState.fields).forEach((f) => {
        let field = this.formState.fields[f]
        if (field._state.autoFillHandled) {
          return
        }
        if (
          field.value &&
          !field.convertedValue &&
          /* the != is important because numbers with dsiplay 0.0 and value 0 */
          field.convertedValue != field.value &&
          field.validationResult.System.length === 0 &&
          field.validationResult.Validate.length === 0
        ) {
          field._state.autoFillHandled = true
          console.info(
            `Ovl AutoFill detected: FieldKey: ${field.fieldKey}, Value: ${field.value}, ConvertedValue: ${field.convertedValue} `
          )
          FieldSetVal(field, field.value)
        }
      })
    }
    // </autofill hack>
  }
}
